import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  MouseEventHandler,
  Ref,
} from 'react';
import type { To } from 'react-router';
import { Link, type LinkProps } from 'react-router-dom';
import type { IconSize } from '../Icon/Icon';
import type { IconGlyph, IconStyle } from '../Icon/IconGlyph';
export type InteractiveAtomSize = 'small' | 'default' | 'large';
export type InteractiveAtomAccent = 'positive' | 'negative';
export type InteractiveAtomIconPosition = 'start' | 'end';

export type InteractiveAtomBaseProps = {
  accent?: InteractiveAtomAccent;
  size?: InteractiveAtomSize;
  loading?: boolean;

  icon?: IconGlyph;
  iconStyle?: IconStyle;
  iconPosition?: InteractiveAtomIconPosition;

  // selected common underlying element props for "official interface"
  className?: string;
  disabled?: boolean;
};

export type InteractiveAtomRenderedAsButtonProps<T> = T & {
  renderAs: 'button';
  forwardRef?: Ref<HTMLButtonElement>;
  // selected underlying element props for "official interface"
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onMouseDown?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'reset' | 'submit';
  // underlying 'button' element props for advanced usage scenarios
  elementProps?: ButtonHTMLAttributes<HTMLButtonElement>;
};
export type InteractiveAtomRenderedAsAnchorProps<T> = T & {
  renderAs: 'anchor';
  forwardRef?: Ref<HTMLAnchorElement>;
  // selected underlying element props for "official interface"
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  onMouseDown?: MouseEventHandler<HTMLAnchorElement>;
  href: string;
  // underlying 'a' element props for advanced usage scenarios
  elementProps?: AnchorHTMLAttributes<HTMLAnchorElement>;
};
export type InteractiveAtomRenderedAsLinkProps<T> = T & {
  renderAs: 'link';
  forwardRef?: Ref<HTMLAnchorElement>;
  // selected underlying element props for "official interface"
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  onMouseDown?: MouseEventHandler<HTMLAnchorElement>;
  to: To;
  // underlying 'Link' element props for advanced usage scenarios
  elementProps?: LinkProps;
};
export type InteractiveAtomRenderedAsDivProps<T> = T & {
  renderAs: 'div';
  forwardRef?: Ref<HTMLDivElement>;
  // selected underlying element props for "official interface"
  onClick?: MouseEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
  // underlying 'div' element props for advanced usage scenarios
  elementProps?: HTMLAttributes<HTMLDivElement>;
};

type InteractiveAtomRenderAs = 'button' | 'anchor' | 'link' | 'div';
type InteractiveAtomRenderAsType = 'button' | 'a' | typeof Link | 'div';

export const fromRenderAsToElement: {
  [Key in InteractiveAtomRenderAs]: InteractiveAtomRenderAsType;
} = {
  button: 'button',
  anchor: 'a',
  link: Link,
  div: 'div',
};

export const fromInteractiveAtomToIconSize: {
  [Key in InteractiveAtomSize]: IconSize;
} = {
  small: 'xs',
  default: 'sm',
  large: 'base',
};
